import type { UtilRequestConfig } from '../../types';
import type { ActorDto } from '../ActorsService/types';

export type TalkingHeadActorCloneRequest = {
	client_secret: string;
	connector_idn: string;
	external_id: string;
	to_connector_idn: string;
	to_integration_idn: string;
};

export type TalkingHeadSettingsRequest = {
	client_secret: string;
};

export type TalkingHeadSettings = {
	agent_name: string;
	agent_picture_url: string;
	auto_open_after: number;
	avatar_id: string;
	bubble_text: string;
	client_secret: string;
	client_website_url: string;
	color: string;
	connector_idn: string;
	disable_talking_head: boolean;
	greeting_phrase: string;
	icon_position_x: number;
	icon_position_y: number;
	show_watermark: boolean;
	showcase_enabled: boolean;
	voice_id: string;
};

export type CreateTalkingHeadActorRequest = Pick<
	ActorDto,
	'connector_idn' | 'external_id' | 'name'
> & {
	client_secret: string;
};

export type TalkingHeadArgument = {
	name: string;
	value: string;
};

export type TalkingHeadCommand = {
	act_id?: string;
	arguments: TalkingHeadArgument[];
	command_idn: string;
	connector_idn?: string;
	external_event_id?: string;
	integration_idn?: string;
};

export enum TalkingHeadActorEventIdn {
	AgentRequest = 'agent_request',
	ContactConsentReceived = 'contact_consent_received',
	CreateCustomer = 'create_customer',
	DeleteItem = 'delete_item',
	ExperienceRatingSubmitted = 'experience_rating_submitted',
	ExtractData = 'extract_data',
	GetVibeConsultation = 'get_vibe_consultation',
	InputResponse = 'input_response',
	InitCanvas = 'init_canvas',
	InitLibrary = 'init_library',
	InterruptChat = 'interrupt_chat',
	MessageFeedbackSubmitted = 'message_feedback_submitted',
	OnboardingStarted = 'onboarding_started',
	RequestTransactions = 'request_transactions',
	RevertCheckpoint = 'revert_to_checkpoint',
	RevertTransaction = 'revert_transaction',
	SaveCanvas = 'save_canvas',
	SaveItem = 'save_item',
	SetActiveChatActor = 'set_active_chat_actor',
	UserMessage = 'user_message'
}

export enum TalkingHeadCommandIdn {
	AgentInfo = 'agent_info',
	NotifyError = 'notify_error',
	OnboardingError = 'onboarding_error',
	OnboardingFinished = 'onboarding_finished',
	ReceiveTransactionsState = 'update_transactions',
	RevertCheckpointSuccess = 'revert_to_checkpoint_success',
	UpdateCanvas = 'update_canvas',
	UpdateChatTitle = 'update_chat_title',
	UpdateItem = 'update_item',
	UpdateLibrary = 'update_library'
}

export enum TalkingHeadAgentInfoState {
	Start = 'start',
	Initial = 'initial',
	Parsing = 'parsing',
	Created = 'created',
	Error = 'error'
}

export type TalkingHeadActorEventBase = {
	arguments: TalkingHeadArgument[];
	event_idn: TalkingHeadActorEventIdn;
	external_id: string;
};

export type TalkingHeadActorEventRequest = TalkingHeadActorEventBase & {
	client_secret: string | null;
	connector_idn: string;
	user_actor_id?: string;
};

export type TalkingHeadSendMessageRequest = {
	client_secret: string;
	connector_idn: string;
	event_idn: TalkingHeadActorEventIdn.UserMessage;
	external_id: string;
	text: string;
	user_actor_id: string;
};

export type AgentCreatorSendActorEventRequest = TalkingHeadActorEventBase;

export type AgentCreatorSendActorEventResponse = {
	act_id?: string;
	external_event_id?: string;
};

export enum TalkingHeadTrainingSourceType {
	Website = 'website',
	Text = 'text'
}

export enum TalkingHeadTrainingArgumentName {
	Source = 'source',
	BusinessName = 'business_name',
	SourceType = 'source_type',
	UserData = 'user_data',
	CountryCode = 'country_code',
	ForceUseCountryCode = 'force_use_country_code',
	ForceReinitialize = 'force_reinitialize',
	ShouldBeInitialized = 'should_be_initialized',
	ValidateSource = 'validate_source'
}

export enum TalkingHeadCommandArgumentName {
	State = 'state',
	Error = 'error',
	ErrorType = 'error_type',
	ErrorMessage = 'error_message',
	Description = 'description',
	Reason = 'reason'
}

export enum TalkingHeadTrainingErrorType {
	EmptyScrape = 'EmptyScrape',
	InvalidSource = 'InvalidSource',
	InsufficientDescription = 'InsufficientDescription'
}

export type TalkingHeadNotifyErrorCommand = TalkingHeadCommand & {
	command_idn: TalkingHeadCommandIdn.NotifyError;
};

export enum TalkingHeadVibeConsultationArgumentName {
	Url = 'url',
	NewValue = 'new_value',
	ConsultationType = 'consultation_type',
	AttributeIdn = 'attribute_idn'
}

export enum TalkingHeadRevertTransactionArgumentName {
	TransactionId = 'transaction_id'
}

export enum TalkingHeadRevertCheckpointArgumentName {
	CheckpointId = 'checkpoint_id'
}

export enum TalkingHeadInputResponseArgumentName {
	Value = 'value'
}

export enum TalkingHeadMessageFeedbackArgumentName {
	ActId = 'act_id',
	FeedbackType = 'feedback_type',
	FeedbackValue = 'feedback_value'
}

export enum FeedbackType {
	Thumbs = 'thumbs'
}

export enum FeedbackValue {
	Positive = 'positive',
	Negative = 'negative'
}

export enum TalkingHeadExperienceRatingArgumentName {
	RatingType = 'rating_type',
	RatingValue = 'rating_value',
	RatingScale = 'rating_scale',
	Comment = 'comment'
}

export enum TalkingHeadExperienceRatingType {
	Stars = 'stars'
}

export type TalkingHeadRequestConfig = Partial<UtilRequestConfig>;
